import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './core/context/AuthContext'
import Layout from './core/components/Layout'
import Login from './core/components/Login'
import MfaSetup from './core/components/MfaSetup'
import QuickLogin from './core/components/QuickLogin'
import DeviceRegistration from './core/components/DeviceRegistration'
import Dashboard from './core/components/Dashboard'
import Loading from './core/components/Loading'
import PlatformAuthPage from './core/components/PlatformAuthPage'
import ServerSetup from './core/components/ServerSetup'
import ScrollToTop from './core/components/ScrollToTop'
import ErrorBoundary from './core/components/ErrorBoundary'
import { activeDepartment, activeDepartmentUrl, rememberDepartment, takePendingDeepLink } from './core/services/departments'
import { initPushNotifications, isNative, isBiometricAvailable, verifyBiometric, storeBiometricCredential, hasBiometricCredential, getBiometricDeviceId } from './core/services/native'
import MovedRedirect from './core/components/MovedRedirect'

// Lazy load module pages
const ReportsPage = lazy(() => import('./modules/reports/ReportsPage'))
const TimeTrackerPage = lazy(() => import('./modules/time/TimeTrackerPage'))
const ApparatusPage = lazy(() => import('./modules/apparatus/ApparatusPage'))
const SettingsLayout = lazy(() => import('./settings/SettingsLayout'))
const AnalyticsPage = lazy(() => import('./modules/analytics/AnalyticsPage'))
const CalendarPage = lazy(() => import('./modules/calendar/CalendarPage'))
const BillingPage = lazy(() => import('./modules/billing/BillingPage'))
const PersonnelPage = lazy(() => import('./modules/personnel/PersonnelPage'))
const GearPage = lazy(() => import('./modules/gear/GearPage'))

const PaymentPage = lazy(() => import('./modules/billing/public/PaymentPage'))
const PaymentLookupPage = lazy(() => import('./modules/billing/public/PaymentLookupPage'))
const HelpPage = lazy(() => import('./help/HelpPage'))

// Feeds the current path to the error boundary so it can drop a stale error
// when the user navigates away. Kept as its own component so only this
// subscribes to location changes, rather than re-rendering all of App.
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation()
  return <ErrorBoundary pathname={pathname}>{children}</ErrorBoundary>
}

function App() {
  const { isAuthenticated, isLoading, isPinDevice, getAccessToken, enrollBiometric, user, mfaSetupRequired, completeMfaSetup } = useAuth()
  const navigate = useNavigate()
  // On native, require server setup before showing anything
  const [serverReady, setServerReady] = useState(
    !isNative() || !!localStorage.getItem('scenelightServerUrl')
  )
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false)
  const [biometricEnrolling, setBiometricEnrolling] = useState(false)
  const [biometricLabel, setBiometricLabel] = useState('biometrics')
  const [biometricResult, setBiometricResult] = useState(null) // { type: 'success'|'error', text }

  // Register for push on native at every sign-in (a shared iPad changes
  // hands without restarting the app; see initPushNotifications).
  useEffect(() => {
    if (isAuthenticated && isNative()) {
      initPushNotifications({
        getToken: getAccessToken,
        navigate
      })
      // A notification from another department switched to it; open what it was about
      const deepLink = takePendingDeepLink()
      if (deepLink) navigate(deepLink)
    }
  }, [isAuthenticated])

  // Keep the connected department in the device's list (departments.js), with
  // its name and code: phones connected before the list existed have neither,
  // and the code is how a notification finds its department.
  useEffect(() => {
    if (!isNative() || !serverReady) return
    const url = activeDepartmentUrl()
    if (!url) return
    rememberDepartment({ url })
    if (activeDepartment()?.code && activeDepartment()?.name) return
    fetch('/api/health', { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then(h => { if (h) rememberDepartment({ url, name: h.departmentName, code: h.departmentCode }) })
      .catch(() => {})
  }, [serverReady])

  // Show biometric enrollment prompt after login on native
  useEffect(() => {
    if (!isAuthenticated || !isNative()) return
    if (localStorage.getItem('biometricPromptDismissed')) return

    ;(async () => {
      try {
        const { isAvailable, biometryType } = await isBiometricAvailable()
        if (!isAvailable) return

        const hasCredential = await hasBiometricCredential()
        if (hasCredential) return // already enrolled

        setBiometricLabel(biometryType === 2 ? 'Face ID' : 'Fingerprint')
        setShowBiometricPrompt(true)
      } catch (e) {
        console.log('Biometric prompt check failed:', e)
      }
    })()
  }, [isAuthenticated])

  const handleBiometricEnroll = async () => {
    setBiometricEnrolling(true)
    setBiometricResult(null)
    try {
      const verified = await verifyBiometric('Verify to enable biometric login')
      if (!verified) {
        setBiometricEnrolling(false)
        return
      }

      const deviceId = getBiometricDeviceId()
      const result = await enrollBiometric(deviceId)

      if (result.success) {
        const username = user?.email || user?.name || 'user'
        const stored = await storeBiometricCredential(username, result.token)
        if (!stored) {
          setBiometricResult({ type: 'error', text: 'Failed to save credentials to device.' })
          setBiometricEnrolling(false)
          return
        }
        setBiometricResult({ type: 'success', text: `${biometricLabel} login enabled!` })
        setBiometricEnrolling(false)
        setTimeout(() => {
          setShowBiometricPrompt(false)
          setBiometricResult(null)
        }, 1500)
        return
      } else {
        setBiometricResult({ type: 'error', text: result.error || 'Enrollment failed. Please try again.' })
      }
    } catch (e) {
      console.error('Biometric enrollment failed:', e)
      setBiometricResult({ type: 'error', text: 'Enrollment failed. Please try again.' })
    }
    setBiometricEnrolling(false)
  }

  const dismissBiometricPrompt = () => {
    localStorage.setItem('biometricPromptDismissed', 'true')
    setShowBiometricPrompt(false)
  }

  // On native, show server setup if no department URL is configured yet
  if (!serverReady) {
    // Reload once the department is chosen. Auth starts the moment the app
    // opens -- before there is a server to ask -- so its sign-in options came
    // back empty and the first sign-in screen said "Could not load sign-in
    // options". A fresh start reads the stored server from the beginning.
    return <ServerSetup onConnected={() => { setServerReady(true); window.location.reload() }} />
  }

  // Platform auth must run before the loading guard — it stores the cross-launch
  // JWT and redirects, so it needs to execute before any auth checks.
  if (window.location.pathname === '/platform-auth') {
    return <PlatformAuthPage />
  }

  if (isLoading) {
    return <Loading fullScreen message="Initializing..." />
  }

  return (
    <Suspense fallback={<Loading fullScreen />}>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />

        {/* Platform admin cross-launch (reads JWT from hash fragment) */}
        <Route path="/platform-auth" element={<PlatformAuthPage />} />

        {/* Public payment portal (no auth required) */}
        <Route path="/pay/:token" element={<PaymentPage />} />
        <Route path="/pay" element={<PaymentLookupPage />} />

        {/* Device registration (always accessible) */}
        <Route path="/register-device" element={
          isAuthenticated ? <Navigate to="/" replace /> : <DeviceRegistration />
        } />

        {/* Protected routes */}
        <Route path="/*" element={
          isAuthenticated && mfaSetupRequired ? (
            <MfaSetup onComplete={completeMfaSetup} />
          ) : isAuthenticated ? (
            <Layout>
              <RouteErrorBoundary>
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />

                  {/* Module routes */}
                  <Route path="/reports/*" element={<ReportsPage />} />
                  <Route path="/time/*" element={<TimeTrackerPage />} />
                  <Route path="/apparatus/*" element={<ApparatusPage />} />
                  <Route path="/analytics/*" element={<AnalyticsPage />} />
                  <Route path="/calendar/*" element={<CalendarPage />} />
                  <Route path="/billing/*" element={<BillingPage />} />
                  <Route path="/personnel/*" element={<PersonnelPage />} />
                  <Route path="/equipment/*" element={<GearPage />} />
                  {/* Old URLs kept working: preserves the sub-path and query. */}
                  <Route path="/gear/*" element={<MovedRedirect from="/gear" to="/equipment" />} />
                  <Route path="/truck-check/*" element={<MovedRedirect from="/truck-check" to="/apparatus" />} />
                  <Route path="/settings/truck-check/*" element={<MovedRedirect from="/settings/truck-check" to="/settings/apparatus" />} />
                  <Route path="/help/truck-check/*" element={<MovedRedirect from="/help/truck-check" to="/help/apparatus" />} />

                  {/* Settings routes */}
                  <Route path="/settings/*" element={<SettingsLayout />} />

                  {/* Help */}
                  <Route path="/help/*" element={<HelpPage />} />

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </RouteErrorBoundary>
            </Layout>
          ) : (
            isPinDevice ? <QuickLogin /> : <Navigate to="/login" replace />
          )
        } />
      </Routes>

      {/* Biometric enrollment prompt modal */}
      {showBiometricPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-sm p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white text-center">
              Enable {biometricLabel} Login?
            </h3>
            <p className="text-slate-400 text-sm text-center">
              Sign in quickly next time using {biometricLabel} instead of entering your password.
            </p>
            {biometricResult && (
              <div className={`p-3 rounded-lg text-sm text-center ${
                biometricResult.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}>
                {biometricResult.text}
              </div>
            )}
            {biometricResult?.type !== 'success' && (
              <div className="flex gap-3">
                <button
                  onClick={dismissBiometricPrompt}
                  disabled={biometricEnrolling}
                  className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                >
                  Not Now
                </button>
                <button
                  onClick={handleBiometricEnroll}
                  disabled={biometricEnrolling}
                  className="flex-1 py-2.5 btn btn-primary font-medium text-sm disabled:opacity-50"
                >
                  {biometricEnrolling ? 'Setting up...' : biometricResult?.type === 'error' ? 'Retry' : 'Enable'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Suspense>
  )
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <h1 className="text-4xl font-bold text-slate-400 mb-4">404</h1>
      <p className="text-slate-500">Page not found</p>
    </div>
  )
}

export default App
